import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { api } from '../services/api';

export default function Billing() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    Promise.all([
      api.getBillingStatus().catch(() => null),
      api.getInvoices().catch(() => ({ invoices: [] })),
    ]).then(([status, inv]) => {
      setBilling(status);
      setInvoices(inv?.invoices || []);
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { url } = await api.createPortalSession();
      window.location.href = url;
    } catch { /* ignore */ }
    finally { setPortalLoading(false); }
  };

  const isPremium = user.tier === 'premium';

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'usd',
    }).format(amount / 100);
  };

  return (
    <div className="min-h-screen pt-24 px-6 pb-16">
      <div className="max-w-2xl mx-auto">
        <h1 className="font-serif text-2xl text-warm mb-8">{t('billing.title')}</h1>

        {/* Current Plan */}
        <div className="border border-dark-200/50 rounded-xl p-5 mb-6">
          <h2 className="text-warm-100/60 text-xs uppercase tracking-wider mb-3">{t('billing.currentPlan')}</h2>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-xs ${isPremium ? 'bg-gold/20 text-gold' : 'bg-dark-200 text-warm-100/60'}`}>
                {isPremium ? 'Premium' : 'Free'}
              </span>
              {billing?.subscriptionStatus && (
                <span className="text-warm-100/40 text-xs">{billing.subscriptionStatus}</span>
              )}
            </div>
            {isPremium && (
              <button
                onClick={handleManageBilling}
                disabled={portalLoading}
                className="text-warm-100/60 text-sm hover:text-warm transition-colors"
              >
                {portalLoading ? t('account.opening') : t('billing.manageBilling')}
              </button>
            )}
          </div>
        </div>

        {/* Invoices */}
        <div className="border border-dark-200/50 rounded-xl p-5">
          <h2 className="text-warm-100/60 text-xs uppercase tracking-wider mb-4">{t('billing.invoices')}</h2>
          {loading ? (
            <p className="text-warm-100/40 text-sm">{t('billing.loading')}</p>
          ) : invoices.length === 0 ? (
            <p className="text-warm-100/40 text-sm">{t('billing.noInvoices')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-warm-100/40 text-xs uppercase tracking-wider border-b border-dark-200/30">
                    <th className="text-left py-2 pr-4">{t('billing.date')}</th>
                    <th className="text-left py-2 pr-4">{t('billing.description')}</th>
                    <th className="text-left py-2 pr-4">{t('billing.amount')}</th>
                    <th className="text-left py-2 pr-4">{t('billing.status')}</th>
                    <th className="text-left py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id} className="border-b border-dark-200/20">
                      <td className="py-3 pr-4 text-warm-100/60">
                        {new Date(inv.date * 1000).toLocaleDateString()}
                      </td>
                      <td className="py-3 pr-4 text-warm">{inv.description}</td>
                      <td className="py-3 pr-4 text-warm">{formatAmount(inv.amount, inv.currency)}</td>
                      <td className="py-3 pr-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          inv.status === 'paid' ? 'bg-green-500/15 text-green-400' :
                          inv.status === 'open' ? 'bg-yellow-500/15 text-yellow-400' :
                          'bg-dark-200 text-warm-100/60'
                        }`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex gap-2">
                          {inv.hosted_invoice_url && (
                            <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                              className="text-gold/60 hover:text-gold text-xs flex items-center gap-1">
                              {t('billing.invoice')} <ExternalLink size={10} />
                            </a>
                          )}
                          {inv.invoice_pdf && (
                            <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                              className="text-gold/60 hover:text-gold text-xs flex items-center gap-1">
                              {t('billing.pdf')} <ExternalLink size={10} />
                            </a>
                          )}
                          {inv.receipt_url && (
                            <a href={inv.receipt_url} target="_blank" rel="noopener noreferrer"
                              className="text-gold/60 hover:text-gold text-xs flex items-center gap-1">
                              {t('billing.receipt')} <ExternalLink size={10} />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
